import {
  type PendingAttachmentState,
  type PendingAttachmentStatus,
} from "@/atoms/messagesAtoms.ts";
import { errorToast } from "@/components/ui/toast.tsx";
import { isAxiosError } from "axios";
import { updateThreadOptimistically } from "@/hooks/useThreads.tsx";
import { uploadFile } from "@/lib/fileUpload.ts";
import type { NewMessageReq } from "@/types/threadEvents.ts";
import type { QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import type { AttachedFile } from "./types.ts";

/** Jotai setter for `pendingThreadAttachmentsAtom` (from `useSetAtom`). */
export type SetPendingAttachments = (
  update: (prev: Record<number, PendingAttachmentState>) => Record<number, PendingAttachmentState>,
) => void;

/** Seed the overlay for a thread with all files in the `uploading` state. */
export const seedPendingAttachments = (
  setPending: SetPendingAttachments,
  threadId: number,
  tempClientId: number,
  attachedFiles: AttachedFile[],
): void => {
  setPending((prev) => ({
    ...prev,
    [threadId]: {
      tempClientId,
      files: attachedFiles.map((af) => ({
        id: af.id,
        name: af.file.name,
        preview: af.preview,
        status: "uploading" as const,
      })),
    },
  }));
};

const patchThread = (
  setPending: SetPendingAttachments,
  threadId: number,
  patch: (state: PendingAttachmentState) => PendingAttachmentState,
): void => {
  setPending((prev) => {
    const state = prev[threadId];
    if (!state) return prev;
    return { ...prev, [threadId]: patch(state) };
  });
};

const setAllStatus = (
  setPending: SetPendingAttachments,
  threadId: number,
  status: PendingAttachmentStatus,
): void => {
  patchThread(setPending, threadId, (state) => ({
    ...state,
    files: state.files.map((f) => ({ ...f, status, errorMessage: undefined })),
  }));
};

/**
 * Backstop for a `summarizeFile` job that never resolves the chips — e.g. it
 * crashes and exhausts Cloud Tasks retries without ever emitting the
 * thread_event:update that lands the file artifacts. Without this the chips
 * would spin on "analyzing" forever. After the timeout, any still-`analyzing`
 * file flips to an error state. This is self-healing: if the job finishes late,
 * the artifacts still arrive and the reconciler clears the overlay entirely.
 */
const ANALYZING_TIMEOUT_MS = 120_000;

const scheduleAnalyzingTimeout = (
  setPending: SetPendingAttachments,
  threadId: number,
  tempClientId: number | undefined,
): void => {
  setTimeout(() => {
    patchThread(setPending, threadId, (state) => {
      // A newer send to this thread replaced the overlay — its chips belong to a
      // different message, so this stale timer must not touch them.
      if (state.tempClientId !== tempClientId) return state;
      return {
        ...state,
        files: state.files.map((f) =>
          f.status === "analyzing"
            ? { ...f, status: "error", errorMessage: "Processing timed out" }
            : f,
        ),
      };
    });
  }, ANALYZING_TIMEOUT_MS);
};

/** Remove a thread's overlay entry (call once the real file artifacts arrive). */
export const clearPendingAttachments = (
  setPending: SetPendingAttachments,
  threadId: number,
): void => {
  setPending((prev) => {
    if (!(threadId in prev)) return prev;
    const { [threadId]: _removed, ...rest } = prev;
    return rest;
  });
};

/**
 * Upload each attachment's bytes to GCS, returning the resulting fileIds. Marks
 * the failing file `error` in the overlay and throws on the first failure
 * (matching the previous foreground behavior — we don't POST a partial set).
 */
export const uploadAttachments = async (
  attachedFiles: AttachedFile[],
  threadId: number,
  setPending: SetPendingAttachments,
): Promise<string[]> => {
  const fileIds: string[] = [];
  for (const af of attachedFiles) {
    try {
      const { fileId } = await uploadFile(af.file);
      fileIds.push(fileId);
    } catch (error) {
      console.error("Attachment upload failed:", error);
      // A 400 from `/api/a/files/upload` means the type was rejected server-side —
      // the attach-time check should normally catch this first (see hooks.ts),
      // but toast a clear reason here so a rejection is never an opaque failure.
      const status = isAxiosError(error) ? error.response?.status : undefined;
      errorToast(
        status === 400
          ? `"${af.file.name}" can't be attached — unsupported file type`
          : `Couldn't upload "${af.file.name}". Please try again.`,
      );
      patchThread(setPending, threadId, (state) => ({
        ...state,
        files: state.files.map((f) =>
          f.id === af.id ? { ...f, status: "error", errorMessage: "Upload failed" } : f,
        ),
      }));
      // Deliberately a fresh Error rather than the original: the toast above owns
      // this failure and already words it for the user. Letting the axios error
      // through would also hand the send path a server reason to show inline, so
      // one rejected upload would be described twice, differently — the raw
      // `Unsupported file type: … Supported types: <every MIME we accept>` beside
      // the toast's short version. The composer keeps its generic summary here.
      throw new Error(`Failed to upload ${af.file.name}`);
    }
  }
  return fileIds;
};

/**
 * Upload attachments then POST the message — used by BOTH send paths after the
 * optimistic message is already on screen. Flips the overlay to `analyzing`
 * once uploads finish (the `summarizeFile` job is now running server-side). The
 * caller owns the optimistic message + overlay seeding and error rollback.
 */
export const uploadThenPostMessage = async (
  threadId: number,
  attachedFiles: AttachedFile[],
  // `body.tempClientId` must already be set by the caller's onMutate so the
  // server can reconcile the real message back to the optimistic placeholder.
  body: NewMessageReq,
  setPending: SetPendingAttachments,
): Promise<void> => {
  const fileIds = await uploadAttachments(attachedFiles, threadId, setPending);
  setAllStatus(setPending, threadId, "analyzing");
  await api.post(`/api/a/threads/${threadId}/messages`, { ...body, fileIds });
  // Don't let the chips spin forever if the summarize job never lands artifacts.
  // Scoped to this send's tempClientId so a later send to the same thread isn't
  // affected by this timer.
  scheduleAnalyzingTimeout(setPending, threadId, body.tempClientId);
};

/**
 * Upload attachments for a caller that POSTs them somewhere other than the
 * messages endpoint (the review's batch commit), then flip the chips to
 * `analyzing` once `post` lands. Same overlay lifecycle as
 * {@link uploadThenPostMessage}; the caller seeds the chips first.
 */
export const uploadThenPost = async <T>(
  threadId: number,
  tempClientId: number,
  attachedFiles: AttachedFile[],
  setPending: SetPendingAttachments,
  post: (fileIds: string[]) => Promise<T>,
): Promise<T> => {
  const fileIds = await uploadAttachments(attachedFiles, threadId, setPending);
  setAllStatus(setPending, threadId, "analyzing");
  const result = await post(fileIds);
  scheduleAnalyzingTimeout(setPending, threadId, tempClientId);
  return result;
};

/**
 * Detached controller for the home-page "create thread → navigate → send" flow.
 * Runs after the OmniBox unmounts, so it owns its own error handling: on failure
 * it marks the overlay `error`, reverts the optimistic running state, and stashes
 * a `retry` that re-runs the whole upload+send.
 */
export const runBackgroundUploadAndSend = (params: {
  threadId: number;
  attachedFiles: AttachedFile[];
  /** Must include `tempClientId` for socket reconciliation. */
  body: NewMessageReq;
  setPending: SetPendingAttachments;
  queryClient: QueryClient;
  /**
   * Runs once the message has been posted. Only then: a caller that wants to
   * remember what was sent must not remember a send that failed, and this
   * controller outlives the composer that started it, so the composer can't
   * await the result itself.
   */
  onSent?: () => void;
}): void => {
  const { threadId, attachedFiles, body, setPending, queryClient, onSent } = params;

  const run = async () => {
    await uploadThenPostMessage(threadId, attachedFiles, body, setPending);
    onSent?.();
  };

  void run().catch((error) => {
    console.error("Background attachment send failed:", error);
    patchThread(setPending, threadId, (state) => ({
      ...state,
      files: state.files.map((f) =>
        f.status === "error" ? f : { ...f, status: "error", errorMessage: "Send failed" },
      ),
      retry: () => {
        setAllStatus(setPending, threadId, "uploading");
        updateThreadOptimistically(queryClient, threadId, { activityState: "running" });
        runBackgroundUploadAndSend(params);
      },
    }));
    updateThreadOptimistically(queryClient, threadId, { activityState: "stopped" });
  });
};
